import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { ONBOARDING_KEY } from "@/api/hooks/use-onboarding";

export interface ProviderKeyInfo {
  provider: string;
  last4: string | null;
  created_at: string | null;
}

export interface GatewayKeyInfo {
  id: string;
  prefix: string;
  created_at: string | null;
  revoked_at: string | null;
}

interface RotateResult {
  gateway_key: string;
  key: GatewayKeyInfo;
}

const PROVIDER_KEYS_KEY = ["settings-provider-keys"];
const GATEWAY_KEYS_KEY = ["settings-gateway-keys"];

export function useProviderKeys() {
  return useQuery({
    queryKey: PROVIDER_KEYS_KEY,
    queryFn: () =>
      api.get("settings/provider-keys").json<{ providers: ProviderKeyInfo[] }>(),
  });
}

export function useSaveProviderKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, apiKey }: { provider: string; apiKey: string }) =>
      api
        .put(`settings/provider-keys/${provider}`, { json: { api_key: apiKey } })
        .json<ProviderKeyInfo>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROVIDER_KEYS_KEY });
      // Step 1 of guided setup just completed — refresh the onboarding guide so
      // it checks the step off and unlocks step 2 without waiting for a refocus.
      qc.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useGatewayKeys() {
  return useQuery({
    queryKey: GATEWAY_KEYS_KEY,
    queryFn: () => api.get("settings/gateway-keys").json<{ keys: GatewayKeyInfo[] }>(),
  });
}

export function useRotateGatewayKey() {
  const qc = useQueryClient();
  return useMutation({
    // The full key in the result is deliberately NOT written into any query
    // cache — the caller holds it in component state, shows it once, drops it.
    mutationFn: (opts: { revokeExisting: boolean }) =>
      api
        .post("settings/gateway-keys/rotate", {
          json: { revoke_existing: opts.revokeExisting },
        })
        .json<RotateResult>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GATEWAY_KEYS_KEY });
      // Creating a gateway key is the explicit "step 2 done" signal; the server
      // stamps onboarding complete on rotate (when a provider key is on file),
      // so refresh the guide to reflect the finished state immediately.
      qc.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useRevokeGatewayKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, confirmLockout }: { keyId: string; confirmLockout: boolean }) =>
      api
        .post(`settings/gateway-keys/${keyId}/revoke`, {
          json: { confirm_lockout: confirmLockout },
        })
        .json<{ status: string }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GATEWAY_KEYS_KEY });
    },
  });
}
